import moment, { type Moment } from 'moment';

import { createFile } from '#apps/client-web/features/file-storage/helpers';
import { type FileContent } from '#apps/client-web/features/file-storage/services/file-storage';

import type { Attachment } from '#packages/data-collection/models/common/attachment';
import { type AuditBackgroundForm } from '#packages/data-collection/models/audit-background-form';
import { isNil } from '#packages/data-collection/libs/lodash';

import { emptyFormData } from './constants';
import { type EditAuditFormBackgroundData } from './types';

const DATETIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';

export const momentToFormDate = (momentObject: Moment): string =>
  momentObject.format(DATETIME_LOCAL_FORMAT);

export const formDateToMoment = (value: string): Moment =>
  moment(value, DATETIME_LOCAL_FORMAT, true);

export const fromFormData = (
  formData: EditAuditFormBackgroundData,
): Omit<AuditBackgroundForm, 'type'> => ({
  general: {
    ...formData.general,
    siteVisitStartDate: isNil(formData.general.siteVisitStartDate)
      ? undefined
      : formDateToMoment(formData.general.siteVisitStartDate),
    siteVisitEndDate: isNil(formData.general.siteVisitEndDate)
      ? undefined
      : formDateToMoment(formData.general.siteVisitEndDate),
    meters: (formData.general.meters ?? []).map(({ attachments, ...rest }) => ({
      ...rest,
      attachments: attachments?.map(({ name, mimeType }) => ({
        name,
        mimeType,
      })),
    })),
  },
  envelope: {
    ...formData.envelope,
    windowTypes: {
      ...formData.envelope.windowTypes,
      attachments: formData.envelope.windowTypes.attachments?.map(
        ({ name, mimeType }) => ({ name, mimeType }),
      ),
    },
    doorTypes: {
      ...formData.envelope.doorTypes,
      attachments: formData.envelope.doorTypes.attachments?.map(
        ({ name, mimeType }) => ({ name, mimeType }),
      ),
    },
    wallTypes: {
      ...formData.envelope.wallTypes,
      attachments: formData.envelope.wallTypes.attachments?.map(
        ({ name, mimeType }) => ({ name, mimeType }),
      ),
    },
    roofingTypes: {
      ...formData.envelope.roofingTypes,
      attachments: formData.envelope.roofingTypes.attachments?.map(
        ({ name, mimeType }) => ({ name, mimeType }),
      ),
    },
  },
  controls: {
    ...formData.controls,
    attachments: formData.controls.attachments?.map(({ name, mimeType }) => ({
      name,
      mimeType,
    })),
  },
  spaceHeating: {
    ...formData.spaceHeating,
    systems: (formData.spaceHeating.systems ?? []).filter(
      (s): s is string => s != null,
    ),
  },
  spaceCooling: {
    ...formData.spaceCooling,
    systems: (formData.spaceCooling.systems ?? []).filter(
      (s): s is string => s != null,
    ),
  },
  ventilation: {
    ...formData.ventilation,
  },
  domesticColdWater: {
    ...formData.domesticColdWater,
  },
  domesticHotWater: {
    ...formData.domesticHotWater,
  },
  lighting: {
    ...formData.lighting,
  },
  other: {
    ...formData.other,
  },
});

export const toFormData = (
  data: AuditBackgroundForm | undefined,
  files?: (FileContent | undefined)[],
): EditAuditFormBackgroundData => {
  // Files are ordered to match the flat sequence:
  // meters[0].attachments[0], ..., meters[N].attachments[M], controls.attachments[0], ...
  let fileIndex = 0;

  const rebuildAttachments = (attachments: Attachment[] | undefined) =>
    (attachments ?? []).map((attachment) => ({
      ...attachment,
      file: createFile({
        content: files![fileIndex++]!,
        name: attachment.name,
        mimeType: attachment.mimeType,
      }),
    }));

  const meters = (data?.general.meters ?? []).map((meter) => ({
    ...meter,
    attachments: rebuildAttachments(meter.attachments),
  }));

  const controlsAttachments = rebuildAttachments(data?.controls.attachments);

  const envelopeWindowAttachments = rebuildAttachments(
    data?.envelope.windowTypes.attachments,
  );
  const envelopeDoorAttachments = rebuildAttachments(
    data?.envelope.doorTypes.attachments,
  );
  const envelopeWallAttachments = rebuildAttachments(
    data?.envelope.wallTypes.attachments,
  );
  const envelopeRoofingAttachments = rebuildAttachments(
    data?.envelope.roofingTypes.attachments,
  );

  return {
    general: isNil(data?.general)
      ? {
          ...emptyFormData.general,
        }
      : {
          ...data.general,
          meters,
          siteVisitStartDate: isNil(data.general.siteVisitStartDate)
            ? undefined
            : momentToFormDate(data.general.siteVisitStartDate),
          siteVisitEndDate: isNil(data.general.siteVisitEndDate)
            ? undefined
            : momentToFormDate(data.general.siteVisitEndDate),
        },
    envelope: isNil(data?.envelope)
      ? {
          ...emptyFormData.envelope,
        }
      : {
          ...data.envelope,
          windowTypes: {
            ...data.envelope.windowTypes,
            attachments: envelopeWindowAttachments,
          },
          doorTypes: {
            ...data.envelope.doorTypes,
            attachments: envelopeDoorAttachments,
          },
          wallTypes: {
            ...data.envelope.wallTypes,
            attachments: envelopeWallAttachments,
          },
          roofingTypes: {
            ...data.envelope.roofingTypes,
            attachments: envelopeRoofingAttachments,
          },
        },
    controls: isNil(data?.controls)
      ? { ...emptyFormData.controls }
      : { ...data.controls, attachments: controlsAttachments },
    spaceHeating: isNil(data?.spaceHeating)
      ? {
          ...emptyFormData.spaceHeating,
        }
      : { ...data.spaceHeating },
    spaceCooling: isNil(data?.spaceCooling)
      ? { ...emptyFormData.spaceCooling }
      : { ...data.spaceCooling },
    ventilation: isNil(data?.ventilation)
      ? {
          ...emptyFormData.ventilation,
        }
      : data.ventilation,
    domesticColdWater: isNil(data?.domesticColdWater)
      ? { ...emptyFormData.domesticColdWater }
      : { ...data.domesticColdWater },
    domesticHotWater: isNil(data?.domesticHotWater)
      ? { ...emptyFormData.domesticHotWater }
      : { ...data.domesticHotWater },
    lighting: isNil(data?.lighting)
      ? {
          ...emptyFormData.lighting,
        }
      : data.lighting,
    other: isNil(data?.other)
      ? {
          ...emptyFormData.other,
        }
      : data.other,
  };
};
